
import { defineComponent } from 'vue';
import { IHost } from '@/types/models';
import { ResourceType } from '@/types/enums';
import api from '@/api/my/hosts';
import agentPing from '@/api/agent/ping';
import agentInstall from '@/api/agent/install';
import agentAddSite from '@/api/agent/addSite';
import QRCode from 'qrcode';
import utils from '@/utils';
import Backups from './components/hosts/Backups.vue';
import Domains from './components/hosts/Domains.vue';
import Empty from './components/hosts/Empty.vue';

interface IUpgradeForm {
  hostType: string;
  amount: number;
}

interface IPayForm {
  resourceType: string;
  hostId: number;
  hostType: string;
  year: number;
  amount: number;
  message: string;
  channel: string;
  isPurchased: boolean;
  isQrCode: boolean;
  orderNo: string;
  failureCount: 0;
}

interface INameForm {
  id: number;
  nickName: string;
}

interface IConnection {
  name: string;
  description: string;
  docUrl: string;
}

interface IInstallForm {
  securityKey: string;
  themeName: string;
  themeDownloadUrl: string;
  guid: string;
  userName: string;
  password: string;
  confirmPassword: string;
}

interface IState {
  hosts: IHost[] | null;
  host: IHost;
  priceStarter: number;
  priceStandard: number;
  priceProfessional: number;
  discount1Year: number;
  discount2Year: number;
  discount3Year: number;
  discount4Year: number;
  discount5Year: number;
  isPay: boolean;
  payForm: IPayForm;
  isNameForm: boolean;
  nameForm: INameForm;
  isConnections: boolean;
  connections: IConnection[];
  isBackups: boolean;
  isUpgrade: boolean;
  upgradeForm: IUpgradeForm;
  isDomains: boolean;
  isInstallOrResetPassword: boolean;
  isInstall: boolean;
  isResetPassword: boolean;
  installForm: IInstallForm;
}

export default defineComponent({
  components: {
    Backups,
    Domains,
    Empty,
  },
  data() {
    return {
      hosts: null,
      host: {} as IHost,
      priceStarter: 0,
      priceStandard: 0,
      priceProfessional: 0,
      discount1Year: 0,
      discount2Year: 0,
      discount3Year: 0,
      discount4Year: 0,
      discount5Year: 0,
      isPay: false,
      payForm: {} as IPayForm,
      isNameForm: false,
      nameForm: {} as INameForm,
      isConnections: false,
      connections: [],
      isBackups: false,
      isUpgrade: false,
      upgradeForm: {} as IUpgradeForm,
      isDomains: false,
      isInstallOrResetPassword: false,
      isInstall: false,
      isResetPassword: false,
      installForm: {} as IInstallForm,
    } as IState;
  },
  methods: {
    apiGet(pingHostId?: number) {
      api.get((res) => {
        this.hosts = res.hosts;
        this.priceStarter = res.priceStarter;
        this.priceStandard = res.priceStandard;
        this.priceProfessional = res.priceProfessional;
        this.discount1Year = res.discount1Year;
        this.discount2Year = res.discount2Year;
        this.discount3Year = res.discount3Year;
        this.discount4Year = res.discount4Year;
        this.discount5Year = res.discount5Year;

        for (const host of this.hosts) {
          host.pingPongCount = 0;
          host.isPingPong = true;
          if (!host.isInstalled || host.id === pingHostId) {
            host.isPingPong = false;
            this.apiPing(host);
          }
        }
      });
    },

    apiPing(host: IHost) {
      agentPing.ping(host.hostName, host.nodeName, host.isHongKong, (pong: string) => {
        if (pong === 'pong') {
          host.isPingPong = true;
          host.pingPongCount = 0;
        } else {
          host.isPingPong = false;
          host.pingPongCount += 1;
          if (host.pingPongCount < 20) {
            setTimeout(() => {
              this.apiPing(host);
            }, 3000);
          }
        }
      });
    },

    getChannel() {
      return this.payForm.channel === 'AliPay' ? '支付宝' : '微信';
    },

    getHostName(host: IHost) {
      return utils.getHostUniqueName(host);
    },

    getHostType(host: IHost) {
      return utils.getHostType(host.hostType);
    },

    getUrl(hostName: string, nodeName: string, isHongKong: boolean): string {
      return utils.getHostUrl(hostName, nodeName, isHongKong);
    },

    getUniqueName(host: IHost): string {
      return utils.getHostUniqueName(host);
    },

    isExpiration(host: IHost): boolean {
      const date = new Date();
      date.setDate(date.getDate() + 15);
      const expirationDate = new Date(host.expirationDate);
      return expirationDate.getTime() <= date.getTime();
    },

    btnNameClick(host: IHost) {
      this.nameForm.id = host.id;
      this.nameForm.nickName = host.nickName;
      this.isNameForm = true;
    },

    btnPayCloseClick() {
      this.isPay = false;
      this.isUpgrade = false;
      this.payForm.isPurchased = false;
      this.payForm.isQrCode = false;
      this.apiGet();
    },

    btnUpgradeSubmitClick() {
      this.payForm.resourceType = ResourceType.HostUpgrade;
      this.payForm.hostId = this.host.id;
      this.payForm.hostType = this.upgradeForm.hostType;
      this.payForm.year = 0;
      this.payForm.amount = this.upgradeForm.amount;
      this.payForm.failureCount = 0;
      this.isPay = true;
    },

    getPayDescription() {
      if (this.isUpgrade) {
        const hostType = utils.getHostType(this.upgradeForm.hostType);
        const amount = this.upgradeForm.amount.toFixed(2);
        return `
          升级版本:
          <span style="color: #fa5300; margin-right: 15px">${hostType}</span>
          费用合计:
          <span style="color: #fa5300">￥${amount}</span>`;
      }
      return '';
    },

    payQuery() {
      const interval = setInterval(() => {
        api.query(
          {
            orderNo: this.payForm.orderNo,
          },
          (res) => {
            this.payForm.isPurchased = res.isPurchased;
            this.payForm.message = res.message;

            if (this.payForm.isPurchased) {
              clearInterval(interval);
              setTimeout(() => {
                this.btnPayCloseClick();
              }, 3000);
            }
          },
        );
      }, 3000);
    },

    btnPayChannelClick(channel: string) {
      this.payForm.channel = channel;

      api.pay(
        {
          resourceType: this.payForm.resourceType,
          hostId: this.payForm.hostId,
          hostType: this.payForm.hostType,
          year: this.payForm.year,
          channel: this.payForm.channel,
          amount: this.payForm.amount,
        },
        (res) => {
          if (!res) return;

          this.payForm.isQrCode = true;
          this.payForm.orderNo = res.orderNo;

          setTimeout(() => {
            QRCode.toCanvas(document.getElementById('qrcode'), res.qrCodeUrl, { width: 260 });
          }, 100);

          this.payQuery();
        },
        (error) => {
          console.log(error);
          this.payForm.failureCount += 1;
          if (this.payForm.failureCount < 3) {
            this.btnPayChannelClick(channel);
          } else {
            utils.loading(false);
            utils.error(error);
          }
        },
      );
    },

    btnNameSubmitClick() {
      api.name(
        {
          id: this.nameForm.id,
          nickName: this.nameForm.nickName,
        },
        (res) => {
          if (!res) return;
          this.isNameForm = false;
          this.apiGet();
        },
      );
    },

    btnConnectionClick(host: IHost) {
      api.credentialsGet(host.id, (res) => {
        this.connections = utils.getHostConnections(
          host,
          res.isHongKong,
          res.securityKey,
          res.ftpPassword,
          res.databaseHost,
          res.databaseUser,
          res.databasePassword,
          res.databaseName,
        );
        this.isConnections = true;
      });
    },

    btnMoreClick(args: { name: string; host: IHost }) {
      this.host = { ...args.host };
      if (args.name === 'Domains') {
        this.isDomains = true;
      } else if (args.name === 'ResetPassword') {
        this.btnInstallClick(args.host, true);
      } else if (args.name === 'Backups') {
        this.isBackups = true;
      } else if (args.name === 'Upgrade') {
        this.isUpgrade = true;
        this.upgradeForm.hostType = this.host.hostType;
        this.upgradeForm.amount = 0;
      } else if (args.name === 'Start') {
        utils.alertDelete(
          {
            title: '启动实例',
            text: `此操作将启动实例 ${this.getUniqueName(this.host)}，确定吗？`,
            button: '确认启动',
          },
          () => {
            api.start({ hostId: args.host.id }, () => {
              utils.success('实例启动成功！');
              this.apiGet(args.host.id);
            });
          },
        );
      } else if (args.name === 'Stop') {
        utils.alertDelete(
          {
            title: '停止实例',
            text: `此操作将停止实例 ${this.getUniqueName(this.host)}，确定吗？`,
            button: '确认停止',
          },
          () => {
            api.stop({ hostId: args.host.id }, () => {
              utils.success('实例停止成功！');
              this.apiGet();
            });
          },
        );
      } else if (args.name === 'Reload') {
        utils.alertDelete(
          {
            title: '重启实例',
            text: `此操作将重启实例 ${this.getUniqueName(this.host)}，确定吗？`,
            button: '确认重启',
          },
          () => {
            api.reload({ hostId: args.host.id }, () => {
              utils.success('实例重启成功！');
              this.apiGet(args.host.id);
            });
          },
        );
      }
    },

    btnUpgradeHostTypeChanged(hostType: string) {
      if (hostType === this.host.hostType) {
        this.upgradeForm.amount = 0;
      } else {
        api.upgrade(
          {
            hostId: this.host.id,
            hostType: this.upgradeForm.hostType,
          },
          (res) => {
            if (!res) return;
            this.upgradeForm.amount = res.amount;
          },
        );
      }
    },

    btnInstallClick(host: IHost, isResetPassword: boolean) {
      this.host = { ...host };
      api.installGet(host.id, (res) => {
        this.installForm.securityKey = res.securityKey;
        this.installForm.themeName = res.themeName;
        this.installForm.themeDownloadUrl = res.themeDownloadUrl;
        this.installForm.guid = res.guid;
        this.installForm.userName = '';
        this.installForm.password = '';
        this.installForm.confirmPassword = '';
        this.isInstallOrResetPassword = true;
        if (isResetPassword) {
          this.isInstall = false;
          this.isResetPassword = true;
        } else {
          this.isInstall = true;
          this.isResetPassword = false;
        }
      });
    },

    apiInstallSubmit() {
      api.installSubmit(
        {
          hostId: this.host.id,
          userName: this.installForm.userName,
          password: this.installForm.password,
        },
        (res) => {
          if (res.value) {
            if (this.isInstall) {
              utils.success('恭喜，初始化安装成功');
              this.isInstall = false;
            } else if (this.isResetPassword) {
              utils.success('恭喜，重置密码成功');
              this.isResetPassword = false;
            }
            this.isInstallOrResetPassword = false;
            this.isInstall = false;
            this.isResetPassword = false;
            this.apiGet();
            utils.loading(false);
          }
        },
      );
    },

    apiAgentInstall(success: () => void) {
      agentInstall.install(
        this.host.hostName,
        this.host.nodeName,
        this.host.isHongKong,
        {
          securityKey: this.installForm.securityKey,
          userName: this.installForm.userName,
          password: this.installForm.password,
        },
        (res) => {
          if (res.value) {
            success();
          }
        },
      );
    },

    apiAgentAddSite(success: () => void) {
      agentAddSite.addSite(
        this.host.hostName,
        this.host.nodeName,
        this.host.isHongKong,
        {
          securityKey: this.installForm.securityKey,
          siteName: this.installForm.themeName,
          root: true,
          parentId: 0,
          siteDir: this.installForm.themeName,
          themeDownloadUrl: this.installForm.themeDownloadUrl,
          guid: this.installForm.guid,
        },
        (res) => {
          if (res.site) {
            success();
          }
        },
        (errorMessage) => {
          utils.loading(false);
          utils.error(errorMessage);
        },
      );
    },

    btnInstallSubmitClick() {
      utils.loading(true);
      this.apiAgentInstall(() => {
        if (this.installForm.themeDownloadUrl && this.installForm.guid) {
          this.apiAgentAddSite(() => {
            this.apiInstallSubmit();
          });
        } else {
          this.apiInstallSubmit();
        }
      });
    },

    validatePass(rule: string, value: string, callback: (error?: Error) => void) {
      if (value === '') {
        callback(new Error('请再次输入密码'));
      } else if (value !== this.installForm.password) {
        callback(new Error('两次输入密码不一致!'));
      } else {
        callback();
      }
    },
  },
  // mounted() {
  //   this.apiGet();
  // },
  created() {
    this.$watch(
      () => this.$route.query,
      () => {
        this.apiGet();
      },
      // fetch the data when the view is created and the data is
      // already being observed
      { immediate: true },
    );
  },
});
